// ─────────────────────────────────────────────────────────────────────────────
// llm_client.ts — OpenAI Chat Completions client for the PWA
//
// Direct browser fetch to the OpenAI API.  Streams responses via
// ReadableStream / SSE.  No server proxy, no VS Code dependency.
//
// Streaming flow:
//   buildMessages() → streamCompletion() → onToken callback
//                                        → accumulated full response
//
// Offline: requests queue as NotificationEntry and drain on reconnect.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  LlmConfig, ChatMessage, StreamToken, OnStreamToken,
} from '@shared/agent_protocol.js';

// ── Token estimation ─────────────────────────────────────────────────────────

/** Rough token estimate: ~4 chars per token for English text */
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

export function messagesTokenEstimate(msgs: ChatMessage[]): number {
  // Each message has ~4 overhead tokens (role, separators)
  return msgs.reduce((sum, m) => sum + (m.tokenEstimate ?? estimateTokens(m.content)) + 4, 0);
}

// ── Build message array with budget trimming ─────────────────────────────────

export interface CompletionRequest {
  messages: ChatMessage[];
  config: LlmConfig;
}

/**
 * Trim conversation history to fit within the context budget.
 * Keeps the system message(s) at the front and the most recent user/assistant
 * messages at the tail, dropping middle messages first.
 */
export function trimToTokenBudget(
  messages: ChatMessage[],
  budgetTokens: number,
): ChatMessage[] {
  const system = messages.filter(m => m.role === 'system');
  const conversation = messages.filter(m => m.role !== 'system');

  const systemCost = messagesTokenEstimate(system);
  let remaining = budgetTokens - systemCost;
  if (remaining <= 0) return system;

  // Walk conversation backwards, keeping recent messages
  const kept: ChatMessage[] = [];
  for (let i = conversation.length - 1; i >= 0; i--) {
    const cost = (conversation[i].tokenEstimate ?? estimateTokens(conversation[i].content)) + 4;
    if (remaining - cost < 0) break;
    remaining -= cost;
    kept.unshift(conversation[i]);
  }

  return [...system, ...kept];
}

// ── Streaming completion ─────────────────────────────────────────────────────

export interface CompletionResult {
  content: string;
  finishReason: string;
  promptTokens: number;
  completionTokens: number;
}

/**
 * Stream a chat completion from the OpenAI-compatible API.
 * Calls `onToken` for each SSE delta — the caller animates text from these.
 * Returns the accumulated full response when the stream ends.
 *
 * Throws on network error or non-2xx response.
 */
export async function streamCompletion(
  request: CompletionRequest,
  onToken: OnStreamToken,
  signal?: AbortSignal,
): Promise<CompletionResult> {
  const { config, messages } = request;

  const trimmed = trimToTokenBudget(messages, config.contextBudgetTokens);

  const body = {
    model: config.model,
    messages: trimmed.map(m => ({ role: m.role, content: m.content })),
    max_tokens: config.maxResponseTokens,
    temperature: config.temperature,
    stream: true,
  };

  const response = await fetch(`${config.baseUrl}/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${config.apiKey}`,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new LlmError(response.status, errorText);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new LlmError(0, 'No response body');

  const decoder = new TextDecoder();
  let accumulated = '';
  let finishReason = 'stop';
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      // Keep the last potentially incomplete line in the buffer
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine || !trimmedLine.startsWith('data: ')) continue;
        const data = trimmedLine.slice(6);
        if (data === '[DONE]') continue;

        try {
          const parsed = JSON.parse(data);
          const delta = parsed.choices?.[0]?.delta?.content;
          const reason = parsed.choices?.[0]?.finish_reason;

          if (delta) {
            accumulated += delta;
            onToken({ delta, finishReason: null });
          }
          if (reason) {
            finishReason = reason;
          }
        } catch {
          // Skip malformed SSE chunks
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  onToken({ delta: '', finishReason: finishReason as StreamToken['finishReason'] });

  return {
    content: accumulated,
    finishReason,
    // Token counts from streaming aren't always available; estimate
    promptTokens: messagesTokenEstimate(trimmed),
    completionTokens: estimateTokens(accumulated),
  };
}

// ── Non-streaming fallback ───────────────────────────────────────────────────

export async function blockingCompletion(
  request: CompletionRequest,
  signal?: AbortSignal,
): Promise<CompletionResult> {
  const { config, messages } = request;
  const trimmed = trimToTokenBudget(messages, config.contextBudgetTokens);

  const body = {
    model: config.model,
    messages: trimmed.map(m => ({ role: m.role, content: m.content })),
    max_tokens: config.maxResponseTokens,
    temperature: config.temperature,
    stream: false,
  };

  const response = await fetch(`${config.baseUrl}/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${config.apiKey}`,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new LlmError(response.status, errorText);
  }

  const json = await response.json();
  const choice = json.choices?.[0];

  return {
    content: choice?.message?.content ?? '',
    finishReason: choice?.finish_reason ?? 'stop',
    promptTokens: json.usage?.prompt_tokens ?? messagesTokenEstimate(trimmed),
    completionTokens: json.usage?.completion_tokens ?? estimateTokens(choice?.message?.content ?? ''),
  };
}

// ── Online detection ─────────────────────────────────────────────────────────

export function isOnline(): boolean {
  return navigator.onLine;
}

// ── Error type ───────────────────────────────────────────────────────────────

export class LlmError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
  ) {
    super(`LLM API error ${status}: ${body.slice(0, 200)}`);
    this.name = 'LlmError';
  }

  get isRateLimit(): boolean { return this.status === 429; }
  get isAuth(): boolean { return this.status === 401; }
  get isServerError(): boolean { return this.status >= 500; }
}
